import React, {useState} from 'react';
import {Form, Modal, Table} from "react-bootstrap";
import {useForm} from "react-hook-form";
import {Link} from "react-router-dom";
import moment from "moment";
import _ from "lodash";
import axios from "../../../helpers/axios";
import sayError from "../../../helpers/sayError";
import numberFormat from "../../../helpers/numberFormat";
import LoadingButton from "../../base/components/loadingButton";
import CustomDatePicker from "../../base/components/forms/customDatePicker";
import BlockUi from "@availity/block-ui";
import AccountedRecordsRow from "./accountedRecordsRow";

const rowKey = (row, index) => row.identifier || `row-${index}`

const AddRowsModal = ({show, onHide, buyerId, excludeId, existingIdentifiers, onAdd}) => {
  const today = moment().format('DD.MM.YYYY')
  const formController = useForm({
    defaultValues: {
      date: today,
      fromBase: true,
      withoutYana: true,
    }
  })
  const {register, handleSubmit} = formController
  const [loading, setLoading] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const [rows, setRows] = useState([])
  const [accountedRecords, setAccountedRecords] = useState([])
  const [info, setInfo] = useState([])
  const [selected, setSelected] = useState({})

  const existingIds = new Set(existingIdentifiers || [])

  const load = ({date, fromBase, withoutYana}) => {
    if (!buyerId || !date) return
    setLoading(true)
    axios.post('/api/salesRegister/getRegister', {
      buyerId,
      start: date,
      end: date,
      fromBase: !!fromBase,
      withoutYana: !!withoutYana,
      ...(excludeId ? {excludeId} : {}),
    })
      .then(({data}) => {
        const nextRows = _.sortBy(data.tableData || [], row => moment(row.issueDate, 'DD.MM.YYYY').unix())
        setRows(nextRows)
        setAccountedRecords(data.accountedRecords || [])
        setInfo(data.info || [])
        setSelected({})
        setLoaded(true)
      })
      .catch(sayError)
      .finally(() => setLoading(false))
  }

  const selectableKeys = rows
    .map((row, index) => ({key: rowKey(row, index), disabled: !!(row.identifier && existingIds.has(row.identifier))}))
    .filter(item => !item.disabled)
    .map(item => item.key)
  const allSelected = selectableKeys.length > 0 && selectableKeys.every(key => selected[key])

  const toggleAll = () => {
    if (allSelected) {
      setSelected({})
      return
    }
    setSelected(Object.fromEntries(selectableKeys.map(key => [key, true])))
  }

  const addSelected = () => {
    const chosen = rows.filter((row, index) => selected[rowKey(row, index)])
    if (!chosen.length) return
    onAdd(chosen)
    onHide()
  }

  return (
    <Modal show={show} onHide={onHide} dialogClassName="modal-90w">
      <Modal.Header closeButton>
        <Modal.Title>Дополнительные строки</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <form onSubmit={handleSubmit(load)} className={'d-flex align-items-center gap-3 flex-wrap'}>
          <div style={{width: 230}}>
            <CustomDatePicker formController={formController}
                              label={"Дата"}
                              value={today}
                              name={'date'}
                              rules={{required: true}}/>
          </div>
          <Form.Label className={'d-flex align-items-center gap-2 mt-2 mb-0'}>
            <input defaultChecked type={"checkbox"} {...register('fromBase')}/>
            Из базы
          </Form.Label>
          <Form.Label className={'d-flex align-items-center gap-2 mt-2 mb-0'}>
            <input defaultChecked type={"checkbox"} {...register('withoutYana')}/>
            Без Яны
          </Form.Label>
          <LoadingButton title={'Получить'} loading={loading} disabled={!buyerId || loading}/>
        </form>
        <BlockUi blocking={loading}>
          {loaded && <>
            {info.length > 0 && <div className={'text-warning mt-3'}>
              {info.map((message, index) => <div key={index}>{message}</div>)}
            </div>}
            {rows.length > 0 ? <div className={'mt-3'} style={{maxHeight: '50vh', overflow: 'auto'}}>
              <Table striped bordered hover className={'mb-2'}>
                <thead>
                <tr>
                  <td>
                    <input type={"checkbox"}
                           checked={allSelected}
                           disabled={!selectableKeys.length}
                           onChange={toggleAll}/>
                  </td>
                  <td>Документ/Операция</td>
                  <td>Пассажир</td>
                  <td>Назначение</td>
                  <td>Описание</td>
                  <td>Дата вылета</td>
                  <td>Итого</td>
                </tr>
                </thead>
                <tbody>
                {rows.map((row, index) => {
                  const key = rowKey(row, index)
                  const alreadyAdded = !!(row.identifier && existingIds.has(row.identifier))
                  return <tr key={key} style={row.transferred ? {backgroundColor: 'rgba(253, 57, 122,0.35)'} : undefined}>
                    <td>
                      <input type={"checkbox"}
                             checked={!!selected[key]}
                             disabled={alreadyAdded}
                             onChange={() => setSelected(prev => ({...prev, [key]: !prev[key]}))}/>
                    </td>
                    <td>
                      {row.bookingId
                        ? <Link to={row.baseLink + row.bookingId} target={'_blank'}>{row.operation}</Link>
                        : row.operation}
                      {alreadyAdded && <span className={'text-muted ml-2'}>уже в реестре</span>}
                    </td>
                    <td>{row.pax}</td>
                    <td style={{whiteSpace: 'nowrap'}}>{row.BSO}</td>
                    <td>{row.route}</td>
                    <td>{row.flightDate}</td>
                    <td className={'text-end'}>{numberFormat(row.total)}</td>
                  </tr>
                })}
                </tbody>
              </Table>
            </div> : <div className={'mt-3'}>За этот день строк нет</div>}
            <div className={'mt-3'}>
              <AccountedRecordsRow accountedRecords={accountedRecords} title={'Учтенные в других реестрах:'}/>
            </div>
          </>}
        </BlockUi>
      </Modal.Body>
      <Modal.Footer>
        <button type={"button"}
                className={'btn btn-primary'}
                disabled={!Object.values(selected).some(Boolean)}
                onClick={addSelected}>
          Добавить выбранное
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default AddRowsModal;
